import styled from "@emotion/styled"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import { Container, CustomBreadcrumbs, NewText, transitionTime } from "ui"
import { transitionEase } from "ui/transitions/utils"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledOuter = styled.div``
// Wrapper that handles hover-zoom for both images and videos
const MediaContainer = styled.div`
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  height: 184px;
  width: 304px;

  img,
  video {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    transition: transform ${transitionTime}s ${transitionEase};
  }

  &:hover img,
  &:hover video {
    transform: scale(1.3);
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 487px;
    height: 260px;
  }
`

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
  margin: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 88px;
    margin-top: 88px;
  }
`

const StyledContainer = styled.div<{ $inView: boolean; $isReversed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  position: relative;
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  flex-direction: column-reverse;
  gap: 24px;
  padding-inline-start: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-inline-start: 0;
    opacity: ${(props) => (props.$inView ? 1 : 0)};
    display: grid;
    grid-template-columns: 1fr 1fr;
    ${MediaContainer} {
      order: ${(props) => (props.$isReversed ? 1 : 0)};
      justify-self: ${(props) => (props.$isReversed ? "flex-start" : "flex-end")};
    }
    gap: 64px;
    flex-direction: ${(props) => (props.$isReversed ? "row-reverse" : "row")};
  }
`

const TextBox = styled.div<{ $isReversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-left: ${({ $isReversed }) => ($isReversed ? "0" : "40px")};
    padding-right: ${({ $isReversed }) => ($isReversed ? "40px" : "0")};
    text-align: ${(p) => (p.$isReversed ? "right" : "left")};
    justify-self: ${(props) => (props.$isReversed ? "flex-end" : "flex-start")};
  }
`

// Simple styled Image that just fills its parent
const StyledImage = styled(Image)`
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
`

const TimelineLine = styled.div`
  position: absolute !important;
  width: 2px;
  height: 100%;
  left: 0;
  top: 0;
  background-image: url("/images/verticalLine.png");
  background-repeat: repeat-y;
  background-size: 2px 720px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 20px;
    background-image: url("/images/verticalLine.png");
    left: 50%;
    transform: translateX(-50%);
  }
`

const StyledPin = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 200px;
  z-index: 1;
  box-sizing: content-box;

  border: 3px solid var(--White, #fff);
  background: var(---, #89c744);
  box-shadow: 0px 0px 16px 0px rgba(166, 207, 81, 0.8);
  position: absolute;
  left: -7px;
  top: 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 20px;
    left: 50%;
    transform: translateX(-50%); // Center the pin horizontally
  }
`

const MobileBusWrapper = styled.div<{ $top: number; $visible: boolean }>`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition: top 0.4s ${transitionEase}, opacity 0.2s ${transitionEase};
  top: ${({ $top }) => `${$top}px`};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  z-index: 2;
`

const StyledNewText = styled(NewText)`
  p {
    margin: 0 !important;
  }
`

export default function ConcreteProcessStep({
  data,
  selfObject,
  parents,
}: {
  data: any
  selfObject: any
  parents: any
}) {
  const concreteProcessSteps = extractProperty(data, "concreteProcessSteps") || []
  const steps = concreteProcessSteps.map((step: any) => {
    return {
      title: extractProperty(step, "title"),
      text: extractProperty(step, "text"),
      stepName: extractProperty(step, "stepName"),
      media: extractProperty(step, "media"),
    }
  }) as {
    title: string
    text: string
    stepName: string
    media: any
  }[]
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const BUS_HEIGHT = 180 // height of bus image in px

  // refs to each container so we can reveal them when bus passes
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  // visible state for each step (revealed by bus)
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>([])

  // bus position and visibility state
  const [busTop, setBusTop] = useState(0)
  const [busVisible, setBusVisible] = useState(false)

  // keep previous busTop in a ref for synchronous access in scroll handler
  const busTopRef = useRef(0)

  // refs to dynamically size the timeline line
  const lineRef = useRef<HTMLDivElement | null>(null)
  const lastPinRef = useRef<HTMLDivElement | null>(null)

  // scroll handler to update bus position relative to viewport
  useEffect(() => {
    const handleScroll = () => {
      if (!layoutRef.current) return
      const layoutTop = layoutRef.current.offsetTop
      const layoutHeight = layoutRef.current.offsetHeight

      const { scrollY, innerHeight: viewportHeight } = window

      // calculate desired top inside layout (candidate position)
      let newTop = scrollY + viewportHeight - layoutTop - BUS_HEIGHT

      // clamp within layout bounds
      newTop = Math.max(0, Math.min(newTop, layoutHeight - BUS_HEIGHT))

      // allow movement only downward
      const onlyDownTop = Math.max(busTopRef.current, newTop)

      if (onlyDownTop !== busTopRef.current) {
        busTopRef.current = onlyDownTop
        setBusTop(onlyDownTop)
      }

      const atBottom = busTopRef.current >= layoutHeight - BUS_HEIGHT - 1
      const inTimelineVertically =
        scrollY + viewportHeight > layoutTop && scrollY < layoutTop + layoutHeight
      setBusVisible(inTimelineVertically && !atBottom)

      // compute bus bottom relative to layout (0 = top of layout)
      const busBottom = newTop + BUS_HEIGHT

      // reveal steps when bus passes their top
      setVisibleSteps((prev) => {
        const next = [...prev]
        stepRefs.current.forEach((stepEl, idx) => {
          if (!stepEl) return
          if (!next[idx] && busBottom >= stepEl.offsetTop) {
            next[idx] = true
          }
        })
        return next
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    // initial call
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // initialize visibleSteps length when steps change
  useEffect(() => {
    setVisibleSteps(new Array(steps.length).fill(false))
  }, [steps.length])

  return (
    <StyledOuter>
      <Container>
        <CustomBreadcrumbs current={selfObject} parents={parents} />

        <StyledLayout ref={layoutRef}>
          <TimelineLine ref={lineRef} />
          <MobileBusWrapper $top={busTop} $visible={busVisible}>
            <Image
              src="/images/bus.png"
              alt="History Timeline Bus"
              style={{ objectFit: "contain" }}
              width={48}
              height={180}
            />
          </MobileBusWrapper>
          {steps.map((el, index) => (
            <StyledContainer
              ref={(node) => {
                stepRefs.current[index] = node
              }}
              $inView={visibleSteps[index]}
              $isReversed={index % 2 === 0}
              key={index}
            >
              <MediaContainer>
                {el.media.includes("mp4") ? (
                  <ReactPlayer
                    url={getUmbracoMediaUrl(el.media)}
                    width={487}
                    height={260}
                    playing={true}
                    loop={true}
                    muted={true}
                    playsinline={true}
                    controls={false}
                  />
                ) : (
                  <StyledImage
                    src={getUmbracoMediaUrl(el.media)}
                    alt={el.title}
                    width={487}
                    height={260}
                  />
                )}
              </MediaContainer>
              <StyledPin ref={index === steps.length - 1 ? lastPinRef : undefined} />
              <TextBox $isReversed={index % 2 === 0}>
                <NewText
                  color={"#537A26"}
                  desktopFontWeight={700}
                  desktopFontSize={32}
                  desktopLineHeight={"normal"}
                  mobileFontSize={24}
                  component={"h2"}
                  style={{ marginBottom: "-8px" }}
                >
                  {el.stepName}
                </NewText>
                <NewText
                  component={"h3"}
                  desktopFontSize={24}
                  mobileFontSize={20}
                  desktopFontWeight={700}
                  desktopLineHeight={"100%"}
                  color="black"
                >
                  {el.title}
                </NewText>
                <StyledNewText
                  component={"p"}
                  desktopFontSize={16}
                  desktopLineHeight={"100%"}
                  color="black"
                  dangerouslySetInnerHTML={{ __html: el.text }}
                />
              </TextBox>
            </StyledContainer>
          ))}
        </StyledLayout>
      </Container>
    </StyledOuter>
  )
}
